import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { Suspense, useEffect, useMemo, useState, type ReactNode } from 'react';
import { initSupabase, isSignOutInFlight } from '@pc/db';
import { reportError } from '@pc/core';
import {
  AppBootOverlay,
  RouteContentLoader,
  RouterErrorElement,
  createAppQueryClient,
  lazyWithReload,
  MfaChallengePage,
  RequireMfaVerified,
} from '@pc/ui';
import { env } from './env';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { ActiveContextProvider, useActiveContext } from './auth/ActiveContextProvider';
import { AuthCallbackPage } from './auth/AuthCallbackPage';
import { OrgStorePicker } from './auth/OrgStorePicker';
import { AppShell } from './shell/AppShell';
import { OrgAppShell } from './shell/OrgAppShell';
import { Dashboard } from './shell/Dashboard';
import { useModules } from './shell/useModules';
import { moduleRegistry } from './shell/moduleRegistry';
import { SettingsLayout } from './settings/SettingsLayout';
import { SettingsDeveloperLayout } from './settings/SettingsDeveloperLayout';
import { QuotaUpsellProvider } from './shell/QuotaUpsellProvider';
import { DevModeProvider } from './devmode/DevModeProvider';
import { ModuleNotOnPlanPage } from './shell/ModuleNotOnPlanPage';
import { TrialExpiredGate } from './shell/TrialExpiredGate';
import { useBillingState } from './shell/useOrgBilling';

// Use `lazyWithReload` instead of `React.lazy` so a stale-shell operator
// that clicks a nav link after a deploy auto-recovers via a debounced
// page reload instead of hitting "Unexpected Application Error! Failed
// to fetch dynamically imported module". See
// packages/ui/src/lazyWithReload.ts.
const SettingsProfilePage = lazyWithReload(
  () => import('./settings/SettingsProfilePage'),
);
const SettingsAccountPage = lazyWithReload(
  () => import('./settings/SettingsAccountPage'),
);
const SettingsSecurityPage = lazyWithReload(
  () => import('./settings/SettingsSecurityPage'),
);
const SettingsIntegrationsPage = lazyWithReload(
  () => import('./settings/SettingsIntegrationsPage'),
);
const SettingsWebhooksPage = lazyWithReload(
  () => import('./settings/SettingsWebhooksPage'),
);
const SettingsApiKeysPage = lazyWithReload(
  () => import('./settings/SettingsApiKeysPage'),
);
const SettingsConnectorsPage = lazyWithReload(
  () => import('./settings/SettingsConnectorsPage'),
);
const SettingsMessagingPage = lazyWithReload(
  () => import('./settings/SettingsMessagingPage'),
);
const SettingsTemplatesPage = lazyWithReload(
  () => import('./settings/SettingsTemplatesPage'),
);
const SettingsPdfsPage = lazyWithReload(() => import('./settings/SettingsPdfsPage'));
const SettingsTeamPage = lazyWithReload(() => import('./settings/SettingsTeamPage'));
const SettingsImportPage = lazyWithReload(
  () => import('./settings/SettingsImportPage'),
);
const SettingsBillingPage = lazyWithReload(
  () => import('./settings/SettingsBillingPage'),
);
const SettingsUsagePage = lazyWithReload(() => import('./settings/SettingsUsagePage'));
const SettingsStorePage = lazyWithReload(() => import('./settings/SettingsStorePage'));
const OrgDashboardPage = lazyWithReload(() => import('./org/OrgDashboardPage'));
const OrgClientsPage = lazyWithReload(() => import('./org/OrgClientsPage'));
const OrgClientInboxPage = lazyWithReload(
  () => import('./org/OrgClientInboxPage'),
);
const OrgOrdersPage = lazyWithReload(() => import('./org/OrgOrdersPage'));
const PublicProposalPage = lazyWithReload(() =>
  import('@pc/modules-orders').then((m) => ({ default: m.PublicProposalPage })),
);
// Public recipient-facing shipment tracking page. Token in the URL is
// the only credential; the page calls the `fulfillment-track-public`
// edge function which forwards to a service-role RPC for a sanitized
// snapshot. See packages/modules/fulfillment/src/public/PublicTrackingPage.tsx.
const PublicTrackingPage = lazyWithReload(() =>
  import('@pc/modules-fulfillment/public').then((m) => ({
    default: m.PublicTrackingPage,
  })),
);
// Public recipient-facing appointment confirm / cancel page rendered at
// /c/:token. Token is minted via calendar.rpc_mint_public_action_token
// and embedded in reminder emails / SMS. The page calls the
// calendar-action-public edge function for sanitized appointment
// details + to apply confirm/cancel without an authenticated session.
const PublicAppointmentActionPage = lazyWithReload(() =>
  import('@pc/modules-calendar/public').then((m) => ({
    default: m.PublicAppointmentActionPage,
  })),
);
const EmailPrefsPage = lazyWithReload(() =>
  import('./email-prefs/EmailPrefsPage').then((m) => ({ default: m.EmailPrefsPage })),
);
const NotFoundPage = lazyWithReload(() => import('./pages/NotFoundPage'));
// Platform-admin-only Dev Mode feature-verification hub (/dev). The page
// gates itself on usePlatformAdmin; mounted in both shells.
const DevModePage = lazyWithReload(() => import('./devmode/DevModePage'));
const PairReferralPage = lazyWithReload(() => import('./pages/PairReferralPage'));

initSupabase({ url: env.supabaseUrl, anonKey: env.supabaseAnonKey });

const queryClient = createAppQueryClient({
  onQueryError: (error, query) => {
    void reportError(error as unknown, {
      severity: 'error',
      tags: {
        source: 'react_query.query',
        query_key: Array.isArray(query.queryKey)
          ? query.queryKey.map((k) => (typeof k === 'string' ? k : JSON.stringify(k))).join(':')
          : String(query.queryKey),
      },
    });
  },
  onMutationError: (error) => {
    void reportError(error as unknown, {
      severity: 'error',
      tags: { source: 'react_query.mutation' },
    });
  },
});

// Central auth: when there is no session, bounce to
// `auth.peptideclients.com/login` with the current full URL serialized
// as `?return_to=`. The central site signs the user in and hands off
// back to `/auth/callback#access_token=...&refresh_token=...&return_to=...`
// which calls `supabase.auth.setSession` and navigates to the original
// in-app path. See `apps/web/src/auth/AuthCallbackPage.tsx`.
const CENTRAL_AUTH_LOGIN_URL = 'https://auth.peptideclients.com/login';

function RequireAuth() {
  const { session, loading } = useAuth();
  // Forces a re-render after the sign-out sentinel TTL expires, in
  // case the expected `window.location.replace` to auth/signout never
  // committed (e.g. browser extension blocked it). Without this tick
  // RequireAuth would be stuck on `<RouteContentLoader />` indefinitely.
  const [signOutTick, setSignOutTick] = useState(0);
  useEffect(() => {
    if (session || loading || !isSignOutInFlight()) return;
    const t = window.setTimeout(() => setSignOutTick((n) => n + 1), 2500);
    return () => window.clearTimeout(t);
  }, [session, loading, signOutTick]);

  if (loading) return null;
  if (!session) {
    // Honor an in-flight sign-out (sentinel set by AuthProvider.signOut
    // BEFORE the @pc/db signOut() call). Without this guard, supabase-js's
    // SIGNED_OUT broadcast triggers a React re-render with session=null
    // while AuthProvider.signOut is still mid-flight; the redirect below
    // then races AuthProvider's intended window.location.replace to
    // auth.peptideclients.com/signout and wins, sending the user to
    // /login with the central session still intact (next nav re-issues
    // a stale token via the "pre-existing session" hand-off).
    if (isSignOutInFlight()) return null;
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams();
      params.set('return_to', window.location.href);
      window.location.replace(`${CENTRAL_AUTH_LOGIN_URL}?${params.toString()}`);
    }
    return null;
  }
  return <Outlet />;
}

/**
 * Org-only gate. Used by the org-mode shell ("Enter organization" entry
 * point) where `activeStoreId` is null but `activeOrgId` must be set.
 */
function RequireOrgContext() {
  const { activeOrgId, mode, ready, hasOrgWideAccess } = useActiveContext();
  if (!ready) return null;
  if (!activeOrgId) return <Navigate to="/choose-context" replace />;
  if (mode !== 'org') return <Navigate to="/" replace />;
  // A store-scoped member has no business in the org shell (the aggregate
  // RPCs 42501 for them). Bounce them out to the picker; the provider also
  // reactively downgrades `mode` to 'store' so the router leaves the org
  // subtree entirely. See ActiveContextProvider.hasOrgWideAccess.
  if (!hasOrgWideAccess) return <Navigate to="/choose-context" replace />;
  return <Outlet />;
}

/**
 * Strict gate -- requires BOTH org and store + mode='store'. Backs the
 * legacy per-store shell. Bounces org-mode users back to `/` so they
 * land on the org dashboard instead of dead module routes.
 */
function RequireStoreContext() {
  const { activeOrgId, activeStoreId, mode, ready } = useActiveContext();
  if (!ready) return null;
  if (!activeOrgId || !activeStoreId)
    return <Navigate to="/choose-context" replace />;
  if (mode !== 'store') return <Navigate to="/" replace />;
  return <Outlet />;
}

/**
 * Wraps any route element that should be hidden while the org's
 * subscription is locked out (expired trial, canceled, past_due, etc.).
 * Renders the TrialExpiredGate upsell in place of the original content;
 * /settings/* routes bypass this guard so the user can always reach
 * Billing and account management.
 */
function ModuleRouteGuard({ children }: { children: ReactNode }) {
  const billing = useBillingState();
  if (billing.loading) return <>{children}</>;
  if (billing.isLockedOut) return <TrialExpiredGate />;
  return <>{children}</>;
}

/**
 * Settings route children, shared by the store-mode and org-mode subtrees
 * so /settings/account, /settings/team, /settings/billing, etc. resolve
 * in both shells. The truly org-scoped pages (account, team, billing,
 * usage, messaging, developer/*) work unmodified in either mode; the
 * three store-scoped pages (`store`, `templates`, `import`) render a
 * "Choose a store" CTA when `mode === 'org'` (see the pages themselves).
 */
function settingsChildren() {
  return [
    { index: true, element: <Navigate to="/settings/account" replace /> },
    {
      path: 'profile',
      element: (
        <Suspense fallback={<RouteContentLoader />}>
          <SettingsProfilePage />
        </Suspense>
      ),
    },
    {
      path: 'account',
      element: (
        <Suspense fallback={<RouteContentLoader />}>
          <SettingsAccountPage />
        </Suspense>
      ),
    },
    {
      path: 'security',
      element: (
        <Suspense fallback={<RouteContentLoader />}>
          <SettingsSecurityPage />
        </Suspense>
      ),
    },
    {
      path: 'store',
      element: (
        <Suspense fallback={<RouteContentLoader />}>
          <SettingsStorePage />
        </Suspense>
      ),
    },
    // Legacy /settings/<dev-tab> URLs -> consolidated Developer parent.
    { path: 'integrations', element: <Navigate to="/settings/developer/integrations" replace /> },
    { path: 'webhooks', element: <Navigate to="/settings/developer/webhooks" replace /> },
    { path: 'api-keys', element: <Navigate to="/settings/developer/api-keys" replace /> },
    { path: 'connectors', element: <Navigate to="/settings/developer/connectors" replace /> },
    {
      path: 'developer',
      element: <SettingsDeveloperLayout />,
      children: [
        { index: true, element: <Navigate to="/settings/developer/api-keys" replace /> },
        {
          path: 'api-keys',
          element: (
            <Suspense fallback={<RouteContentLoader />}>
              <SettingsApiKeysPage />
            </Suspense>
          ),
        },
        {
          path: 'webhooks',
          element: (
            <Suspense fallback={<RouteContentLoader />}>
              <SettingsWebhooksPage />
            </Suspense>
          ),
        },
        {
          path: 'connectors',
          element: (
            <Suspense fallback={<RouteContentLoader />}>
              <SettingsConnectorsPage />
            </Suspense>
          ),
        },
        {
          path: 'integrations',
          element: (
            <Suspense fallback={<RouteContentLoader />}>
              <SettingsIntegrationsPage />
            </Suspense>
          ),
        },
      ],
    },
    {
      path: 'messaging',
      element: (
        <Suspense fallback={<RouteContentLoader />}>
          <SettingsMessagingPage />
        </Suspense>
      ),
    },
    {
      path: 'templates',
      element: (
        <Suspense fallback={<RouteContentLoader />}>
          <SettingsTemplatesPage />
        </Suspense>
      ),
    },
    {
      path: 'pdfs',
      element: (
        <Suspense fallback={<RouteContentLoader />}>
          <SettingsPdfsPage />
        </Suspense>
      ),
    },
    {
      path: 'team',
      element: (
        <Suspense fallback={<RouteContentLoader />}>
          <SettingsTeamPage />
        </Suspense>
      ),
    },
    {
      path: 'import',
      element: (
        <Suspense fallback={<RouteContentLoader />}>
          <SettingsImportPage />
        </Suspense>
      ),
    },
    {
      path: 'billing',
      element: (
        <Suspense fallback={<RouteContentLoader />}>
          <SettingsBillingPage />
        </Suspense>
      ),
    },
    {
      path: 'usage',
      element: (
        <Suspense fallback={<RouteContentLoader />}>
          <SettingsUsagePage />
        </Suspense>
      ),
    },
  ];
}

function AppRouter() {
  const modules = useModules();
  // Read mode here so the router rebuilds when an operator flips between
  // store mode and org mode. The cost (brief unmount of the shell + a
  // TanStack cache invalidation cascade) is desirable: it clears stale
  // per-store state so an org-mode dashboard never accidentally reuses
  // a query from a single-store session, and vice-versa.
  const { mode } = useActiveContext();
  // Calendar is the one module we mount under BOTH shells. In org mode
  // we expose only its public route surface so the org sidebar has a
  // working Calendar link from day one (the module is already org-aware
  // -- appointments.store_id is nullable and listAppointments handles
  // ctx.storeId === null natively).
  const calendarModule = useMemo(
    () => modules.find((m) => m.id === 'calendar') ?? null,
    [modules],
  );
  const productsModule = useMemo(
    () => modules.find((m) => m.id === 'products') ?? null,
    [modules],
  );
  // Messaging is org-scoped (conversations carry org_id, never a store),
  // so its routes mount under BOTH shells. Without this, the email
  // "View conversation" deep link 404s for any session whose last mode
  // was 'org'.
  const messagingModule = useMemo(
    () => modules.find((m) => m.id === 'messaging') ?? null,
    [modules],
  );

  const router = useMemo(
    () =>
      createBrowserRouter([
        {
          // Root layout route: every child inherits this errorElement so a
          // render-time throw inside any module page (e.g. the
          // MissingActiveContextError raised by useActiveCtx when an iOS
          // in-app browser wipes localStorage) is caught by our branded
          // RouterErrorElement instead of react-router's default
          // stack-trace screen. The bare <Outlet /> here is the pass-through
          // -- no UI of its own.
          errorElement: <RouterErrorElement />,
          children: [
        // Hand-off destination from auth.peptideclients.com. MUST live
        // outside RequireAuth (we don't have a session yet at this URL).
        { path: '/auth/callback', element: <AuthCallbackPage /> },
        {
          // Public client-facing proposal approval -- no auth required.
          path: '/p/:token',
          element: (
            <Suspense fallback={<RouteContentLoader />}>
              <PublicProposalPage />
            </Suspense>
          ),
        },
        {
          // Public recipient-facing tracking page -- no auth required.
          // Token is the 256-bit random string minted via
          // fulfillment.rpc_mint_public_tracking_token from the
          // authenticated ShipmentDetailPage. The page calls the
          // fulfillment-track-public edge function for a sanitized
          // shipment snapshot.
          path: '/t/:token',
          element: (
            <Suspense fallback={<RouteContentLoader />}>
              <PublicTrackingPage />
            </Suspense>
          ),
        },
        {
          // Public recipient-facing appointment confirm/cancel page --
          // no auth required. Token is minted via
          // calendar.rpc_mint_public_action_token and embedded in
          // reminder emails / SMS.
          path: '/c/:token',
          element: (
            <Suspense fallback={<RouteContentLoader />}>
              <PublicAppointmentActionPage />
            </Suspense>
          ),
        },
        {
          // Public email preferences / unsubscribe page. Reachable from
          // every email PeptideClients sends; the :token in the URL is the
          // recipient's stable unsubscribe token (see `core.email_prefs`).
          // No auth required by design (legal compliance + UX).
          path: '/email-prefs/:token',
          element: (
            <Suspense fallback={<RouteContentLoader />}>
              <EmailPrefsPage />
            </Suspense>
          ),
        },
        {
          // Bare /email-prefs without a token -- show the not-valid state
          // rather than the catch-all 404 redirect, so a user who lands
          // here from a paste-stripped link gets a useful message.
          path: '/email-prefs',
          element: (
            <Suspense fallback={<RouteContentLoader />}>
              <EmailPrefsPage />
            </Suspense>
          ),
        },
        {
          element: <RequireAuth />,
          children: [
            // /mfa MUST live outside RequireMfaVerified or we get a loop:
            // user lands on /mfa, gate sees they're not verified, redirects
            // to /mfa, gate sees not verified, ad infinitum.
            { path: '/mfa', element: <MfaChallengePage /> },
            {
              element: <RequireMfaVerified />,
              children: [
                { path: '/choose-context', element: <OrgStorePicker /> },
                // -------- ORG MODE SUBTREE --------
                // Mounted when `mode === 'org'`. Slim shell, hardcoded
                // sidebar (Dashboard / Calendar / Clients / Orders /
                // Settings), combined-list pivot pages. All other
                // module paths fall through to NotFoundPage in this
                // mode -- there's no `/wholesalers`, `/shipping`, etc.,
                // when the operator hasn't picked a store.
                ...(mode === 'org'
                  ? [
                      {
                        element: <RequireOrgContext />,
                        children: [
                          {
                            element: <OrgAppShell />,
                            children: [
                              {
                                index: true,
                                element: (
                                  <ModuleRouteGuard>
                                    <Suspense fallback={<RouteContentLoader />}>
                                      <OrgDashboardPage />
                                    </Suspense>
                                  </ModuleRouteGuard>
                                ),
                              },
                              {
                                path: 'clients',
                                element: (
                                  <ModuleRouteGuard>
                                    <Suspense fallback={<RouteContentLoader />}>
                                      <OrgClientsPage />
                                    </Suspense>
                                  </ModuleRouteGuard>
                                ),
                              },
                              // Org-mode (cross-store) inbox. The
                              // companion thread-detail page at
                              // /clients/inbox/:threadId is store-
                              // scoped; OrgClientInboxPage flips
                              // activeMode='store' on row click so
                              // the router rebuilds with AppShell and
                              // serves the per-store thread page.
                              {
                                path: 'clients/inbox',
                                element: (
                                  <ModuleRouteGuard>
                                    <Suspense fallback={<RouteContentLoader />}>
                                      <OrgClientInboxPage />
                                    </Suspense>
                                  </ModuleRouteGuard>
                                ),
                              },
                              {
                                path: 'orders',
                                element: (
                                  <ModuleRouteGuard>
                                    <Suspense fallback={<RouteContentLoader />}>
                                      <OrgOrdersPage />
                                    </Suspense>
                                  </ModuleRouteGuard>
                                ),
                              },
                              // Calendar is org-aware natively. We
                              // mount the module's existing routes
                              // ('/' agenda, '/new', '/settings',
                              // '/:id') under /calendar so deep links
                              // from emails still resolve.
                              ...((calendarModule?.routes ?? []).map(
                                (r) => ({
                                  path: `calendar${r.path === '/' ? '' : r.path}`,
                                  element: (
                                    <ModuleRouteGuard>
                                      <Suspense fallback={<RouteContentLoader />}>
                                        {r.element}
                                      </Suspense>
                                    </ModuleRouteGuard>
                                  ),
                                }),
                              )),
                              // Products are org-owned (catalog defaults +
                              // per-store entitlements/pricing), so expose
                              // their existing module routes in org mode.
                              ...((productsModule?.routes ?? []).map(
                                (r) => ({
                                  path: `products${r.path === '/' ? '' : r.path}`,
                                  element: (
                                    <ModuleRouteGuard>
                                      <Suspense fallback={<RouteContentLoader />}>
                                        {r.element}
                                      </Suspense>
                                    </ModuleRouteGuard>
                                  ),
                                }),
                              )),
                              // Internal messaging is org-scoped, so the
                              // /messages deep links from notification
                              // emails must resolve in org mode too.
                              ...((messagingModule?.routes ?? []).map(
                                (r) => ({
                                  path: `messages${r.path === '/' ? '' : r.path}`,
                                  element: (
                                    <ModuleRouteGuard>
                                      <Suspense fallback={<RouteContentLoader />}>
                                        {r.element}
                                      </Suspense>
                                    </ModuleRouteGuard>
                                  ),
                                }),
                              )),
                              {
                                path: 'settings',
                                element: <SettingsLayout />,
                                children: settingsChildren(),
                              },
                              {
                                path: 'dev',
                                element: (
                                  <Suspense fallback={<RouteContentLoader />}>
                                    <DevModePage />
                                  </Suspense>
                                ),
                              },
                            ],
                          },
                        ],
                      },
                    ]
                  : []),
                // -------- STORE MODE SUBTREE --------
                ...(mode === 'store'
                  ? [
                      {
                        element: <RequireStoreContext />,
                        children: [
                {
                  element: <AppShell modules={modules} />,
                  children: [
                    {
                      index: true,
                      element: (
                        <ModuleRouteGuard>
                          <Suspense fallback={<RouteContentLoader />}>
                            <Dashboard modules={modules} />
                          </Suspense>
                        </ModuleRouteGuard>
                      ),
                    },
                    {
                      // 72-hour partner referral pairing entry point.
                      // Anyone with an active org context can reach this
                      // page; the server-side RPC enforces the 72-hour
                      // window and owner-role requirements.
                      path: 'pair',
                      element: (
                        <Suspense fallback={<RouteContentLoader />}>
                          <PairReferralPage />
                        </Suspense>
                      ),
                    },
                    {
                      path: 'admin/site',
                      element: <Navigate to="/" replace />,
                    },
                    {
                      path: 'settings',
                      element: <SettingsLayout />,
                      children: settingsChildren(),
                    },
                    {
                      path: 'dev',
                      element: (
                        <Suspense fallback={<RouteContentLoader />}>
                          <DevModePage />
                        </Suspense>
                      ),
                    },
                    ...modules.flatMap((m) =>
                      m.routes.map((r) => ({
                        path: `${m.basePath}${r.path === '/' ? '' : r.path}`,
                        element: (
                          <ModuleRouteGuard>
                            <Suspense fallback={<RouteContentLoader />}>{r.element}</Suspense>
                          </ModuleRouteGuard>
                        ),
                      })),
                    ),
                    // For known-but-disabled modules, surface a friendly
                    // upsell page instead of the catch-all 404 redirect.
                    // ModuleRouteGuard takes precedence when the org's
                    // subscription is locked out so the user sees the
                    // trial-expired upsell instead of a "this module
                    // isn't on your plan" pitch they can't act on.
                    ...moduleRegistry
                      .filter((m) => !modules.some((em) => em.id === m.id))
                      .flatMap((m) => [
                        {
                          path: `${m.basePath}`,
                          element: (
                            <ModuleRouteGuard>
                              <ModuleNotOnPlanPage moduleId={m.id} label={m.name} />
                            </ModuleRouteGuard>
                          ),
                        },
                        {
                          path: `${m.basePath}/*`,
                          element: (
                            <ModuleRouteGuard>
                              <ModuleNotOnPlanPage moduleId={m.id} label={m.name} />
                            </ModuleRouteGuard>
                          ),
                        },
                      ]),
                  ],
                },
              ],
            },
                    ]
                  : []),
              ],
            },
          ],
        },
        {
          // Catch-all 404. Replaces the previous `<Navigate to="/" replace />`
          // which silently bounced bad URLs to the dashboard with no
          // explanation. Now a stale link, mistyped settings page, or
          // disabled-module URL surfaces our branded 404 instead.
          //
          // Lives OUTSIDE the RequireAuth gate so unauthed visitors
          // hitting a bad URL get the branded page directly rather
          // than being kicked to /sign-in -> blank screen. Known-but-
          // not-on-plan module routes are intentionally NOT routed
          // here (see `moduleRegistry.filter(...)` above): those
          // render `ModuleNotOnPlanPage` so the operator sees an
          // upsell instead of "page not found".
          path: '*',
          element: (
            <Suspense fallback={<RouteContentLoader />}>
              <NotFoundPage />
            </Suspense>
          ),
        },
          ],
        },
      ]),
    [modules, mode, calendarModule, productsModule, messagingModule],
  );

  // future.v7_startTransition: auto-wrap every router state update in
  // React.startTransition. Without this, clicking a NavLink that targets a
  // lazy()-loaded route triggers React error #426 ("a component suspended
  // while responding to synchronous input"). Opt-in flag in v6.21+, default
  // in v7. Lives on RouterProvider, not on createBrowserRouter.
  return <RouterProvider router={router} future={{ v7_startTransition: true }} />;
}

function AppBootGate() {
  const { session, loading: authLoading } = useAuth();
  const { ready: contextReady } = useActiveContext();
  const booting = authLoading || (Boolean(session) && !contextReady);
  return (
    <AppBootOverlay active={booting}>
      <AppRouter />
    </AppBootOverlay>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ActiveContextProvider>
          <QuotaUpsellProvider>
            <DevModeProvider>
              <AppBootGate />
            </DevModeProvider>
          </QuotaUpsellProvider>
        </ActiveContextProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
